import React, { useState, useEffect } from 'react';
import { broadbandAPI, catvAPI, playboxtvAPI, imbsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Wifi, Tv, Layers, Plus, Edit2, Search, X, Trash2, Film, RefreshCw, EyeOff, FileText, Upload, Globe } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const TYPES = [
  { key: 'broadband', label: 'Broadband', icon: Wifi,   color: 'var(--accent)', bg: 'var(--cb-e0f2fe)' },
  { key: 'catv',      label: 'CableTV',  icon: Tv,     color: 'var(--primary)', bg: 'var(--cb-dbeafe)' },
  { key: 'playboxtv', label: 'PlayBox TV', icon: Film,  color: 'var(--c-7c3aed)', bg: 'var(--cb-ede9fe)' },
];

// PlayBoxTV packs are vendor-synced only (via "Sync PlayBoxTV Packs"), never hand-created —
// the New Package type-selector only ever offers the two locally-authored services.
const NEW_PACKAGE_TYPES = TYPES.filter(t => t.key !== 'playboxtv');

// Package "type" — what role it plays for a customer, distinct from TYPES (which service
// it belongs to). CATV gets a 3rd option ("Ala Carte") since individual channel packs can
// be sold standalone without a base package; broadband only ever has a base plan or an addon.
const PACKAGE_KIND_LABELS = { base: 'Base', addon: 'Addon', ala_carte: 'Ala Carte' };

// ─── PlayBoxTV pack edit modal — resalePrice/isActive only, since name/amount/
// otts/validity are vendor-controlled and refreshed by Sync, not hand-edited ──
function PlayboxPackModal({ pack, onClose, onSuccess }) {
  const [resalePrice, setResalePrice] = useState(pack.resalePrice ?? '');
  const [isActive, setIsActive] = useState(pack.isActive);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await playboxtvAPI.updatePack(pack._id, { resalePrice, isActive });
      toast.success(`${pack.name} updated`);
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h3>{pack.name}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Vendor amount ₹{pack.vendorAmount} · {pack.validityDays} days
              {pack.otts?.length > 0 && <> · {pack.otts.map(o => o.name).join(', ')}</>}
            </div>
            <div className="form-group">
              <label className="form-label">Resale Price (₹) *</label>
              <input type="number" className="form-control" value={resalePrice} required
                placeholder="Price charged to the customer"
                onChange={e => setResalePrice(e.target.value)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="pb-active" checked={isActive}
                onChange={e => setIsActive(e.target.checked)} />
              <label htmlFor="pb-active" style={{ fontSize: '.88rem' }}>Sellable to customers</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function typeStyle(key) {
  return TYPES.find(t => t.key === key) || TYPES[0];
}

// ─── Unified New Connection Modal ─────────────────────────────────────────────
function NewConnectionModal({ item, onClose, onSuccess }) {
  const isEdit   = !!item;
  const initType = item?.source || 'broadband';

  const [type, setType]       = useState(initType);
  const [loading, setLoading] = useState(false);

  // Netzur package picker — local plans work fully without this; syncing is opt-in.
  const [imbsPkgList, setImbsPkgList]       = useState(null);
  const [imbsPkgLoading, setImbsPkgLoading] = useState(false);
  const [imbsPkgError, setImbsPkgError]     = useState(null);
  const [imbsPkgSearch, setImbsPkgSearch]   = useState('');

  const loadImbsPkgs = async () => {
    setImbsPkgLoading(true);
    setImbsPkgError(null);
    try {
      const { data } = await imbsAPI.getPackages();
      const list = data.data?.data || data.data || (Array.isArray(data.data) ? data.data : []);
      setImbsPkgList(Array.isArray(list) ? list : []);
    } catch (err) {
      setImbsPkgError(err.response?.data?.message || 'Failed to load Netzur packages');
      setImbsPkgList([]);
    } finally { setImbsPkgLoading(false); }
  };

  // Broadband fields
  const [bb, setBb] = useState(item?.source === 'broadband' ? item : {
    name: '', displayName: '', speed: { download: '', upload: '' }, type: '',
    price: '', validityDays: 30, gstPercent: 18, features: [], isActive: true, showToCustomer: true, featuredOnWebsite: false, imbsPackageId: '', pricingTiers: [], linkedCatvSlots: 0,
  });
  // CableTV fields
  const [catv, setCatv] = useState(item?.source === 'catv' ? item : {
    name: '', displayName: '', type: '',
    price: '', gstPercent: 18, isActive: true, showToCustomer: true, featuredOnWebsite: false, pricingTiers: [],
  });

  const [featInput, setFeatInput] = useState('');

  const addFeat = (target, setter) => {
    if (!featInput.trim()) return;
    setter(f => ({ ...f, features: [...(f.features || []), featInput.trim()] }));
    setFeatInput('');
  };
  const removeFeat = (target, setter, i) =>
    setter(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  // Pricing tiers — a quick-fill duration+price shortcut offered at renewal time,
  // on top of (never instead of) the flat monthly price above. Shared handlers for
  // both the broadband and CATV sections since the row shape is identical.
  const addTier = (setter) =>
    setter(f => ({ ...f, pricingTiers: [...(f.pricingTiers || []), { label: '', months: 1, price: '' }] }));
  const removeTier = (setter, i) =>
    setter(f => ({ ...f, pricingTiers: f.pricingTiers.filter((_, idx) => idx !== i) }));
  const setTier = (setter, i, key, val) =>
    setter(f => {
      const tiers = [...f.pricingTiers];
      tiers[i] = { ...tiers[i], [key]: val };
      return { ...f, pricingTiers: tiers };
    });

  const TiersEditor = ({ tiers, setter }) => (
    <div className="form-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="form-label" style={{ marginBottom: 0 }}>Pricing Tiers <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(optional quick-fill at renewal)</span></label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => addTier(setter)}>
          <Plus size={12} style={{ marginRight: 4 }} />Add Tier
        </button>
      </div>
      {(tiers || []).length > 0 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 32px', gap: 8, padding: '2px 0 4px', fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            <span>Label</span><span>Months</span><span>Price ₹</span><span></span>
          </div>
          {tiers.map((t, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 32px', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input className="form-control" value={t.label} placeholder="e.g. 3 Months"
                onChange={e => setTier(setter, i, 'label', e.target.value)} style={{ padding: '5px 10px' }} />
              <input className="form-control" type="number" min={1} value={t.months}
                onChange={e => setTier(setter, i, 'months', e.target.value)} style={{ padding: '5px 8px', textAlign: 'center' }} />
              <input className="form-control" type="number" min={0} value={t.price}
                onChange={e => setTier(setter, i, 'price', e.target.value)} style={{ padding: '5px 8px' }} />
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeTier(setter, i)} style={{ padding: '5px 8px' }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (type === 'broadband') {
        if (isEdit) await broadbandAPI.updatePlan(item._id, bb);
        else        await broadbandAPI.createPlan(bb);
      } else {
        if (isEdit) await catvAPI.updatePackage(item._id, catv);
        else        await catvAPI.createPackage(catv);
      }
      toast.success(isEdit ? 'Updated' : 'Package created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const t = typeStyle(type);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <h3>{isEdit ? `Edit ${typeStyle(initType).label} Package` : 'New Package'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* Service selector — only for new packages */}
            {!isEdit && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {NEW_PACKAGE_TYPES.map(tp => {
                  const Icon = tp.icon;
                  const active = type === tp.key;
                  return (
                    <button
                      key={tp.key} type="button"
                      onClick={() => setType(tp.key)}
                      style={{
                        flex: 1, padding: '12px 8px', borderRadius: 8, cursor: 'pointer',
                        border: `2px solid ${active ? tp.color : 'var(--border)'}`,
                        background: active ? tp.bg : 'var(--bg)',
                        color: active ? tp.color : 'var(--text-muted)',
                        fontWeight: active ? 700 : 400, fontSize: '.85rem',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        transition: 'all .15s',
                      }}
                    >
                      <Icon size={20} />
                      {tp.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Broadband fields ── */}
            {(type === 'broadband') && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: 'var(--cb-e0f2fe)', borderRadius: 6 }}>
                  <Wifi size={15} style={{ color: 'var(--accent)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--accent)', fontSize: '.88rem' }}>Broadband Plan</span>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Plan Code *</label>
                    <input className="form-control" value={bb.name} required placeholder="BB_100_UNLIMITED"
                      onChange={e => setBb(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Name *</label>
                    <input className="form-control" value={bb.displayName} required placeholder="100 Mbps Unlimited"
                      onChange={e => setBb(f => ({ ...f, displayName: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="form-label">Download (Mbps) *</label>
                    <input type="number" className="form-control" value={bb.speed.download} required
                      onChange={e => setBb(f => ({ ...f, speed: { ...f.speed, download: e.target.value } }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Upload (Mbps) *</label>
                    <input type="number" className="form-control" value={bb.speed.upload} required
                      onChange={e => setBb(f => ({ ...f, speed: { ...f.speed, upload: e.target.value } }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount (₹) *</label>
                    <input type="number" className="form-control" value={bb.price} required
                      onChange={e => setBb(f => ({ ...f, price: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Package Role *</label>
                    <select className="form-control" value={bb.type || ''} required
                      onChange={e => setBb(f => ({ ...f, type: e.target.value }))}>
                      <option value="" disabled>— Select —</option>
                      <option value="base">Base</option>
                      <option value="addon">Addon</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Validity (Days) *</label>
                    <input type="number" className="form-control" value={bb.validityDays ?? 30} required
                      onChange={e => setBb(f => ({ ...f, validityDays: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GST %</label>
                    <input type="number" className="form-control" value={bb.gstPercent}
                      onChange={e => setBb(f => ({ ...f, gstPercent: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Linked CableTV Slots <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(0 for a normal plan; e.g. 2 for a "2TV" combo)</span></label>
                    <input type="number" min={0} className="form-control" value={bb.linkedCatvSlots ?? 0}
                      onChange={e => setBb(f => ({ ...f, linkedCatvSlots: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Netzur Sync <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(optional — leave blank for a local-only plan)</span></label>
                    {imbsPkgList === null ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input className="form-control" value={bb.imbsPackageId || ''} placeholder="No Netzur package linked"
                          onChange={e => setBb(f => ({ ...f, imbsPackageId: e.target.value }))} />
                        <button type="button" className="btn btn-secondary btn-sm" onClick={loadImbsPkgs} disabled={imbsPkgLoading}
                          style={{ whiteSpace: 'nowrap' }}>
                          {imbsPkgLoading ? 'Loading…' : 'Browse Netzur'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input className="form-control" style={{ marginBottom: 6 }}
                          placeholder={`Type to filter ${imbsPkgList.length} Netzur packages…`}
                          value={imbsPkgSearch} onChange={e => setImbsPkgSearch(e.target.value)} />
                        <select className="form-control" value={bb.imbsPackageId || ''}
                          onChange={e => setBb(f => ({ ...f, imbsPackageId: e.target.value }))}>
                          <option value="">— Local only (no Netzur sync) —</option>
                          {imbsPkgList
                            .filter(pkg => {
                              const q = imbsPkgSearch.trim().toLowerCase();
                              if (!q) return true;
                              const pkgId = String(pkg.id ?? pkg.package_id ?? '');
                              // Selected package always stays in the list even if it no longer
                              // matches the filter — typing shouldn't make the current choice
                              // vanish out from under the select.
                              if (pkgId === String(bb.imbsPackageId || '')) return true;
                              const pkgName = (pkg.name || pkg.package_name || '').toLowerCase();
                              return pkgName.includes(q) || pkgId.includes(q);
                            })
                            .map((pkg, i) => {
                              const pkgId = String(pkg.id ?? pkg.package_id ?? '');
                              const pkgName = pkg.name || pkg.package_name || `Package ${pkgId}`;
                              return (
                                <option key={pkgId || i} value={pkgId}>
                                  {pkgName} — ₹{pkg.price || pkg.monthly_price || 0}
                                </option>
                              );
                            })}
                        </select>
                        {imbsPkgError && <div style={{ color: 'var(--c-dc2626)', fontSize: '.78rem', marginTop: 4 }}>{imbsPkgError}</div>}
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 24, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="bb-active" checked={bb.isActive}
                        onChange={e => setBb(f => ({ ...f, isActive: e.target.checked }))} />
                      <label htmlFor="bb-active" style={{ fontSize: '.88rem' }}>Active</label>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="bb-show" checked={bb.showToCustomer !== false}
                        onChange={e => setBb(f => ({ ...f, showToCustomer: e.target.checked }))} />
                      <label htmlFor="bb-show" style={{ fontSize: '.88rem' }}>Show to customers</label>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="bb-featured" checked={!!bb.featuredOnWebsite}
                        onChange={e => setBb(f => ({ ...f, featuredOnWebsite: e.target.checked }))} />
                      <label htmlFor="bb-featured" style={{ fontSize: '.88rem' }}>Feature on website</label>
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Features</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input className="form-control" value={featInput} placeholder="Add feature…"
                      onChange={e => setFeatInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeat('bb', setBb))} />
                    <button type="button" className="btn btn-secondary" onClick={() => addFeat('bb', setBb)}>Add</button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(bb.features || []).map((f, i) => (
                      <span key={i} style={{ background: 'var(--cb-e0f2fe)', color: 'var(--accent)', padding: '3px 10px', borderRadius: 20, fontSize: '.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {f} <button type="button" onClick={() => removeFeat('bb', setBb, i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)' }}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <TiersEditor tiers={bb.pricingTiers} setter={setBb} />
              </>
            )}

            {/* ── CableTV fields ── */}
            {(type === 'catv') && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: 'var(--cb-dbeafe)', borderRadius: 6 }}>
                  <Tv size={15} style={{ color: 'var(--primary)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: '.88rem' }}>CableTV Package</span>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Package Code *</label>
                    <input className="form-control" value={catv.name} required
                      onChange={e => setCatv(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Name *</label>
                    <input className="form-control" value={catv.displayName} required
                      onChange={e => setCatv(f => ({ ...f, displayName: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Package Role *</label>
                    <select className="form-control" value={catv.type || ''} required
                      onChange={e => setCatv(f => ({ ...f, type: e.target.value }))}>
                      <option value="" disabled>— Select —</option>
                      <option value="base">Base</option>
                      <option value="addon">Addon</option>
                      <option value="ala_carte">Ala Carte</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price (₹/mo) *</label>
                    <input type="number" className="form-control" value={catv.price} required
                      onChange={e => setCatv(f => ({ ...f, price: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">GST %</label>
                    <input type="number" className="form-control" value={catv.gstPercent}
                      onChange={e => setCatv(f => ({ ...f, gstPercent: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 24, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="catv-active" checked={catv.isActive}
                        onChange={e => setCatv(f => ({ ...f, isActive: e.target.checked }))} />
                      <label htmlFor="catv-active" style={{ fontSize: '.88rem' }}>Active</label>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="catv-show" checked={catv.showToCustomer !== false}
                        onChange={e => setCatv(f => ({ ...f, showToCustomer: e.target.checked }))} />
                      <label htmlFor="catv-show" style={{ fontSize: '.88rem' }}>Show to customers</label>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="catv-featured" checked={!!catv.featuredOnWebsite}
                        onChange={e => setCatv(f => ({ ...f, featuredOnWebsite: e.target.checked }))} />
                      <label htmlFor="catv-featured" style={{ fontSize: '.88rem' }}>Feature on website</label>
                    </span>
                  </div>
                </div>
                <TiersEditor tiers={catv.pricingTiers} setter={setCatv} />
              </>
            )}

          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ background: t.color, borderColor: t.color }}>
              {loading ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${t.label} Package`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PlansPage() {
  useDarkReady();
  const [plans,    setPlans]    = useState([]);
  const [packages, setPackages] = useState([]);
  const [pbPacks,  setPbPacks]  = useState([]);
  const [modal,    setModal]    = useState(null);
  const [pbModal,  setPbModal]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [syncing,  setSyncing]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all'); // all | broadband | catv | playboxtv
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive — narrows search to match only within this set
  const [pdfUploadingId, setPdfUploadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handlePdfUpload = async (item, file) => {
    if (!file) return;
    setPdfUploadingId(item._id);
    try {
      const api = item.source === 'broadband' ? broadbandAPI : catvAPI;
      await api.uploadPdf(item._id, file);
      toast.success('Plan PDF uploaded');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'PDF upload failed');
    } finally {
      setPdfUploadingId(null);
    }
  };

  // Both backends actually deactivate rather than hard-delete (blocked entirely if any
  // customer still references the package/plan) — historical payments/bills/renewals
  // populate this doc for receipts, so removing it outright would break old records.
  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.displayName}"? If it's still assigned to any customer this will be blocked — otherwise it's deactivated and hidden from new assignment.`)) return;
    setDeletingId(item._id);
    try {
      const api = item.source === 'broadband' ? broadbandAPI : catvAPI;
      const { data } = await (item.source === 'broadband' ? api.deletePlan(item._id) : api.deletePackage(item._id));
      toast.success(data.message || 'Removed');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk actions run per-item against the same endpoints the single-item buttons use — no
  // dedicated bulk route, so each item still gets its own "still assigned to a customer?"
  // safety check on delete, and PlayBoxTV packs (no delete button on the card either) only
  // ever get deactivated, never removed.
  const handleBulkDeactivate = async (items) => {
    if (!items.length) return;
    if (!window.confirm(`Deactivate ${items.length} selected package(s)? They'll be hidden from new assignment but existing customers are unaffected.`)) return;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const item of items) {
      try {
        if (item.source === 'broadband') await broadbandAPI.updatePlan(item._id, { isActive: false });
        else if (item.source === 'catv') await catvAPI.updatePackage(item._id, { isActive: false });
        else await playboxtvAPI.updatePack(item._id, { isActive: false });
        ok++;
      } catch { failed++; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    toast[failed ? 'error' : 'success'](`Deactivated ${ok}${failed ? `, ${failed} failed` : ''}`);
    loadAll();
  };

  const handleBulkDelete = async (items) => {
    if (!items.length) return;
    if (!window.confirm(`Remove ${items.length} selected package(s)? Any still assigned to a customer will be skipped and deactivated instead.`)) return;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const item of items) {
      try {
        if (item.source === 'broadband') await broadbandAPI.deletePlan(item._id);
        else await catvAPI.deletePackage(item._id);
        ok++;
      } catch { failed++; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    toast[failed ? 'error' : 'success'](`Removed ${ok}${failed ? `, ${failed} blocked/failed` : ''}`);
    loadAll();
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pb, pc, pp] = await Promise.all([
        broadbandAPI.getPlans({ activeOnly: 'false' }),
        catvAPI.getPackages({ activeOnly: 'false' }),
        playboxtvAPI.getPacks(),
      ]);
      setPlans(pb.data.data);
      setPackages(pc.data.data);
      setPbPacks(pp.data.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSyncPlayboxtv = async () => {
    setSyncing(true);
    try {
      const { data } = await playboxtvAPI.syncPacks();
      toast.success(`Synced — ${data.created} new, ${data.updated} updated`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  // Merge + annotate with source type
  const allItems = [
    ...plans.map(p => ({ ...p, source: 'broadband' })),
    ...packages.map(p => ({ ...p, source: 'catv' })),
    ...pbPacks.map(p => ({
      ...p, source: 'playboxtv',
      displayName: p.name, name: p.vendorPackId, price: p.resalePrice,
    })),
  ].filter(p => {
    if (filter !== 'all' && p.source !== filter) return false;
    if (statusFilter === 'active' && !p.isActive) return false;
    if (statusFilter === 'inactive' && p.isActive) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.displayName || '').toLowerCase().includes(q);
  });

  const selectedItems = allItems.filter(p => selected.has(p._id));
  const selectedDeletable = selectedItems.filter(p => p.source !== 'playboxtv');
  const allVisibleSelected = allItems.length > 0 && allItems.every(p => selected.has(p._id));
  const toggleSelectAllVisible = () => {
    setSelected(prev => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      allItems.forEach(p => next.add(p._id));
      return next;
    });
  };

  const counts = {
    all:        plans.length + packages.length + pbPacks.length,
    broadband:  plans.length,
    catv:       packages.length,
    playboxtv:  pbPacks.length,
  };

  const allMerged = [...plans, ...packages, ...pbPacks];
  const statusCounts = {
    all:      allMerged.length,
    active:   allMerged.filter(p => p.isActive).length,
    inactive: allMerged.filter(p => !p.isActive).length,
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 pk-head">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>New Packages</h2>
          <p className="text-muted text-small pk-sub">Manage CableTV & Broadband packages, plus PlayBoxTV addon packs</p>
        </div>
        <div className="pk-head-btns" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleSyncPlayboxtv} disabled={syncing}>
            <RefreshCw size={14} style={{ marginRight: 5 }} />
            {syncing ? 'Syncing…' : 'Sync PlayBoxTV Packs'}
          </button>
          <button className="btn btn-primary" onClick={() => setModal({ mode: 'new' })}>
            <Plus size={15} style={{ marginRight: 5 }} /> New Package
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar pk-filters" style={{ marginBottom: 16 }}>
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search plans…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {[
          { key: 'all',       label: 'All',       count: counts.all },
          { key: 'broadband', label: 'Broadband', count: counts.broadband },
          { key: 'catv',      label: 'CableTV',  count: counts.catv },
          { key: 'playboxtv', label: 'PlayBox TV', count: counts.playboxtv },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`btn btn-sm pk-chip ${filter === f.key ? 'btn-primary' : 'btn-secondary'}`}>
            {f.label} ({f.count})
          </button>
        ))}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 2px' }} />
        {[
          { key: 'all',      label: 'All',      count: statusCounts.all },
          { key: 'active',   label: 'Active',   count: statusCounts.active },
          { key: 'inactive', label: 'Inactive', count: statusCounts.inactive },
        ].map(f => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            className={`btn btn-sm pk-chip ${statusFilter === f.key ? 'btn-primary' : 'btn-secondary'}`}>
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {!loading && allItems.length > 0 && (
        <div className="pk-bulk" style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px',
          background: selected.size ? 'var(--cb-eff6ff)' : 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} />
            Select all {filter !== 'all' ? `(${allItems.length})` : ''}
          </label>
          {selected.size > 0 && (
            <>
              <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{selected.size} selected</span>
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button className="btn btn-secondary btn-sm" disabled={bulkBusy} onClick={() => handleBulkDeactivate(selectedItems)}>
                  Deactivate Selected
                </button>
                <button className="btn btn-danger btn-sm" disabled={bulkBusy || !selectedDeletable.length}
                  title={!selectedDeletable.length ? 'PlayBox TV packs can only be deactivated here, not removed' : ''}
                  onClick={() => handleBulkDelete(selectedDeletable)}>
                  Delete Selected{selectedDeletable.length !== selected.size ? ` (${selectedDeletable.length})` : ''}
                </button>
                <button className="btn btn-secondary btn-sm" disabled={bulkBusy} onClick={() => setSelected(new Set())}>
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : allItems.length === 0 ? (
        <div className="empty-state">
          <Layers size={32} />
          <h3>No packages found</h3>
          <p>Click <strong>New Package</strong> to add your first plan.</p>
        </div>
      ) : (
        <div className="pk-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {allItems.map(p => {
            const tp = typeStyle(p.source);
            const Icon = tp.icon;
            return (
              <div key={p._id} className="card pk-card" style={{ opacity: p.isActive ? 1 : 0.55, border: selected.has(p._id) ? '1px solid var(--primary)' : undefined }}>
                <div className="card-header">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={selected.has(p._id)} onChange={() => toggleSelected(p._id)}
                        style={{ marginRight: 2 }} />
                      <span style={{ background: tp.bg, color: tp.color, borderRadius: 20, padding: '2px 9px', fontSize: '.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon size={11} /> {tp.label}
                      </span>
                      {p.source === 'playboxtv' ? (
                        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-7c3aed)', background: 'var(--cb-ede9fe)', borderRadius: 20, padding: '2px 8px' }}>
                          Addon · Broadband only
                        </span>
                      ) : p.type ? (
                        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-374151)', background: 'var(--cb-f3f4f6)', borderRadius: 20, padding: '2px 8px' }}>
                          {PACKAGE_KIND_LABELS[p.type] || p.type}
                        </span>
                      ) : (
                        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-92400e)', background: 'var(--cb-fef3c7)', borderRadius: 20, padding: '2px 8px' }}>
                          ⚠ Set role
                        </span>
                      )}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '.97rem' }}>{p.displayName}</div>
                    <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{p.name}</div>
                  </div>
                  <div className="pk-icons" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => (p.source === 'playboxtv' ? setPbModal(p) : setModal({ mode: 'edit', item: p }))}>
                      <Edit2 size={12} />
                    </button>
                    {p.source !== 'playboxtv' && (
                      <button className="btn btn-danger btn-sm" title="Remove"
                        disabled={deletingId === p._id} onClick={() => handleDelete(p)}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  {p.source === 'playboxtv' ? (
                    <>
                      <div className="pk-price" style={{ fontSize: '1.45rem', fontWeight: 800, color: tp.color, marginBottom: 6 }}>
                        {p.price != null ? <>₹{p.price}</> : <span style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--c-d97706)' }}>Price not set</span>}
                      </div>
                      <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                        Vendor amount ₹{p.vendorAmount} · {p.validityDays} days
                      </div>
                      {p.otts?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                          {p.otts.map((o, i) => (
                            <span key={i} style={{ background: tp.bg, color: tp.color, padding: '2px 8px', borderRadius: 12, fontSize: '.72rem' }}>{o.name}</span>
                          ))}
                        </div>
                      )}
                      {!p.isActive && <div style={{ marginTop: 8 }}><span className="badge badge-warning">Not sellable</span></div>}
                    </>
                  ) : (
                    <>
                      <div className="pk-price" style={{ fontSize: '1.45rem', fontWeight: 800, color: tp.color, marginBottom: 6 }}>
                        ₹{p.price}
                        {p.source === 'broadband' ? (
                          <span style={{ fontSize: '.84rem', fontWeight: 400, color: 'var(--text-muted)' }}> / {p.validityDays || 30} days</span>
                        ) : (
                          <span style={{ fontSize: '.84rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>
                        )}
                      </div>
                      {p.speed && (p.speed.download > 0 || p.speed.upload > 0) && (
                        <div style={{ fontSize: '.88rem', fontWeight: 600, marginBottom: 6 }}>
                          ⬇ {p.speed.download} / ⬆ {p.speed.upload} Mbps
                        </div>
                      )}
                      <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                        GST {p.gstPercent}% · Incl. GST: ₹{Math.round(p.price * (1 + p.gstPercent / 100))}
                      </div>
                      {p.pricingTiers?.length > 0 && (
                        <div style={{ fontSize: '.76rem', color: tp.color, marginBottom: 8 }}>
                          {p.pricingTiers.map(t => `${t.label} ₹${t.price}`).join(' · ')}
                        </div>
                      )}
                      {p.features?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {p.features.map((f, i) => (
                            <span key={i} style={{ background: tp.bg, color: tp.color, padding: '2px 8px', borderRadius: 12, fontSize: '.72rem' }}>{f}</span>
                          ))}
                        </div>
                      )}
                      {(!p.isActive || p.showToCustomer === false || p.featuredOnWebsite) && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {!p.isActive && <span className="badge badge-warning">Inactive</span>}
                          {p.showToCustomer === false && (
                            <span className="badge" style={{ background: 'var(--cb-f3f4f6)', color: 'var(--c-6b7280)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <EyeOff size={11} /> Hidden from customers
                            </span>
                          )}
                          {p.featuredOnWebsite && (
                            <span className="badge" style={{ background: 'var(--cb-dcfce7)', color: 'var(--c-15803d)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Globe size={11} /> Featured on website
                            </span>
                          )}
                        </div>
                      )}

                      {/* Plan/tariff PDF shown to customers on the portal */}
                      <div className="pk-pdf" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {p.pdf && (
                          <a href={p.pdf} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                            <FileText size={12} style={{ marginRight: 4 }} /> View PDF
                          </a>
                        )}
                        <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                          <Upload size={12} style={{ marginRight: 4 }} />
                          {pdfUploadingId === p._id ? 'Uploading…' : p.pdf ? 'Replace PDF' : 'Upload PDF'}
                          <input
                            type="file" accept="application/pdf" style={{ display: 'none' }}
                            disabled={pdfUploadingId === p._id}
                            onChange={e => {
                              const file = e.target.files[0];
                              e.target.value = ''; // reset so re-selecting the same file still fires onChange next time
                              handlePdfUpload(p, file);
                            }}
                          />
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <NewConnectionModal
          item={modal.item}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); loadAll(); }}
        />
      )}

      {pbModal && (
        <PlayboxPackModal
          pack={pbModal}
          onClose={() => setPbModal(null)}
          onSuccess={() => { setPbModal(null); loadAll(); }}
        />
      )}
    </div>
  );
}
